import * as React from 'react';

import type {
  IAdminParticipantDetail,
  IAdminParticipantHub
} from '../../api/participant-contracts';
import { recordPath } from '../../components/participant-link';
import type { ISectionNavEntry } from '../../components/section-nav';
import type { IDdDisclosures } from '../../dd';
import { AccountsSection } from './accounts';
import { ActivitySection } from './activity';
import {
  DisclosuresSection,
  SurveyTabDisclosures,
  type CrdName,
  type useDrawnDisclosures
} from './disclosures';
import { disclosureSummaryOf } from './disclosure-summary';
import { ddPartSection } from './due-diligence';
import { figuresPartSection } from './figures';
import { hubMembers } from './household';
import { IdentitySection } from './identity';
import { NotesSection } from './notes';
import { IDENTITY_ANCHOR, type INumberWrites } from './numbers';
import { OverviewSection } from './overview';
import { rankingsPartSection } from './rankings';
import { RecordsSection } from './records';
import { RegistryAnswersSection } from './registry-answers';
import { RegistrationsSection } from './registrations';
import { researchDrawn, ResearchSection } from './research';
import { hasRoster, RosterSection, type IRosterRead } from './roster';
import { SalesforceTasksSection } from './salesforce-tasks';
import { SurveysSection } from './surveys';
import { TeamSection, type ITeamNotes } from './team';
import { WorkItemsSection, type IOpenItemsRead } from './work-items';

/**
 * The hub's sections, as one list — the registry (the advisor record page's F1, K8). `HubBody`
 * builds one `IHubContext` and draws **both** the section nav and the body from
 * `hubSections().filter(drawn)`, so a section the page draws is the one the nav names, in the
 * same place, by construction. Before it the two were kept in step by hand: a `SECTIONS` table, a
 * `navEntries` function with its own conditions, and a body that repeated them.
 *
 * **The order is the advisor record page's U1, and it is the rule a later section joins**:
 * Overview, Veritant research, Surveys, Records, Disclosures, Work items, Identity & numbers,
 * Registry answers, Team, Advisors & principals, Account & access, Registrations, Activity,
 * Salesforce tasks, Notes — Salesforce's sections first, in the Lightning page's order, then the
 * platform's own. A section added later goes in at its place in that order as one entry, and never
 * edits another's; the alternative — each block editing `HubBody` — was a merge conflict per block
 * over one function, and a nav that a block forgot to touch. An anchor keeps its spelling when its
 * entry moves: a copied link must still land.
 *
 * An entry's `drawn` is the condition the page had for it before, moved here unchanged: Team
 * (the teams onboarding plan's R12) only where a member of the household is a team to
 * survey-api, or a write on one said something the re-read would take away (`teamMembers`);
 * Disclosures (SR-216's R4) only where a member has a CRD set — a non-null summary on the hub,
 * decided before any read; Veritant research (F2's R2) only where the account holds
 * `internal_fields.read` (SR-228) and the hub carries a member's staff-field record —
 * `include=internal` answered, and it is handed the blocks drawn inside its
 * parts, in their order where neither has a derived field in the schema (F6's R1: Rankings,
 * `rankingsPartSection`, before F5's Asset & production, `figuresPartSection` — U1's order, which
 * Salesforce's page holds); Advisors & principals (F7's R1) only where a member is a team or a
 * firm, the only kinds a roster names slots for.
 * Every other section is always drawn — Records (F3's R2), Salesforce tasks and Work items
 * (SR-222's F9, after Disclosures) among them, since whether a member's archive or queue holds
 * anything takes a read the hub document does not carry.
 *
 * **A function, not a module constant**, for the demo's reason (CLAUDE.md, "Rules of the code"),
 * though no entry reads the demo since F6 deleted its Rankings & research (F6's R9): the demo's
 * barrel imports this domain's, so an entry drawing a demo export, read while this module is first
 * evaluated, could meet it in its temporal dead zone. A function reads its entries at render, where
 * the demo is whole — the shape the next such entry needs, kept rather than changed twice in a file
 * every block appends to.
 */

/*
 * **A person's record page draws these entries in two lists** (SR-391, `recordSections` in
 * `person-record.tsx`): the record tab — U1's order with the survey sections taken out and
 * Related records after Overview — and a survey tab, Responses and Veritant research narrowed to
 * the survey (`IHubContext.surveyCode`). The entries are the same objects; only which list draws
 * them differs, so a section is still written once.
 */

/** What any section may read — the hub's one document and what the page holds beside it. */
export interface IHubContext {
  hub: IAdminParticipantHub;
  /** The opened participant first, then the rest of the household (`hubMembers`). */
  members: IAdminParticipantDetail[];
  /** The list's search the page was opened from, carried by every way back to it. */
  search: string;
  /** `internal_fields.read`: Veritant research, and the hub read asking for it (SR-228). */
  canReadInternal: boolean;
  /** Counts the page's writes — the cue for the reads that follow a write, never the poll. */
  written: number;
  /** Re-reads the hub after a write, and moves `written`. */
  reread: () => Promise<boolean>;
  /** The number of the read the hub on screen came from, counted as reads begin. */
  hubRead: number;
  /** How many hub reads have begun — a write's answer is the page's until a later one lands. */
  readsBegun: () => number;
  writes: INumberWrites;
  /** The members the Team section draws (`teamMembers`). */
  teams: IAdminParticipantDetail[];
  teamNotes: ITeamNotes;
  drawnDisclosures: ReturnType<typeof useDrawnDisclosures>;
  /** The advisor a CRD names where survey-api sent no name (SR-426) — a person's own name on
   *  their own CRD, or the roster slot holding it as the Advisors / Principals section read it —
   *  a disclosure group's heading. */
  crdName: CrdName;
  /** Whether any member has a CRD set — the Disclosures section's rule. */
  withDisclosures: boolean;
  /** The activity feed's `total` as its read last landed; `undefined` before that. */
  activityTotal: number | undefined;
  onActivityTotal: (total: number) => void;
  /** A record page — a person's, a team's or a firm's (SR-391, `person-record.tsx`): the record
   *  opened alone, its surveys as tabs. False: the household's page, an `org`'s. */
  recordPage: boolean;
  /** The survey tab drawn, on a person's record page; absent on the record tab and the
   *  household's page. */
  surveyCode?: string;
  /** The hub as read, household and all — what a person's record page narrowed `hub` from, and
   *  what its Related records names; `hub` itself on the household's page. */
  asRead: IAdminParticipantHub;
  /** A firm's or organisation's record page, whose SEC ADV section draws the register's entry —
   *  Registry answers then draws no SEC ADV register group (SR-407). */
  secAdvElsewhere?: boolean;
  /** The roster's title on a team's or firm's record page (SR-391); absent elsewhere. */
  rosterTitle?: string;
  /** Takes each roster part's read as it lands — the record summary's lead (SR-400). */
  onRoster?: (participantId: string, read: IRosterRead) => void;
  /** Takes each member's first page of open work items — the summary's compliance line. */
  onOpenItems?: (participantId: string, read: IOpenItemsRead) => void;
  /** Those reads as handed up, by member — the DD block's open compliance items (SR-460). */
  openItems?: Readonly<Record<string, IOpenItemsRead>>;
  /** Counts the Actions menu's "New task" presses (SR-391). */
  newTaskAsked: number;
  /** A survey tab's staff-field sections as last drawn, for the nav (SR-474); null before they
   *  are, or off a survey tab. */
  researchEntries: Nullable<ISectionNavEntry[]>;
  /** Takes them as the survey tab's Veritant research draws them. */
  onResearchEntries: (entries: ISectionNavEntry[]) => void;
}

export interface IHubSection {
  /** Names the entry among the others; React's key. */
  key: string;
  /** The section's `id`, which the nav scrolls to. */
  anchor: string;
  /** The nav's words for it. */
  label: string;
  /** Absent: always drawn. */
  drawn?: (ctx: IHubContext) => boolean;
  /** The nav's entries where the section draws several sections of its own — a survey tab's
   *  Veritant research, one per staff-form group (SR-474). Absent, or null: the one entry. */
  entries?: (ctx: IHubContext) => Nullable<ISectionNavEntry[]>;
  /** The nav's count beside the label (F11's Q3, ruled (a)) — only where the number is
   *  survey-api's or the hub document holds the list whole: Surveys, Activity, Notes. A list read
   *  per member (Records, Disclosures, Work items, Salesforce tasks, the rosters) counts nothing
   *  here: its parts carry survey-api's counts, and a sum would be a second count beside them.
   *  Absent, or `undefined`: no count. */
  count?: (ctx: IHubContext) => number | undefined;
  /** Draws the section with `anchor` as its `id` — the entry's own, handed back so it is
   *  spelled once. */
  render: (ctx: IHubContext, anchor: string) => React.ReactNode;
}

/** The notes' anchor, which "Write a note" scrolls to. */
export const NOTES_ANCHOR = 'notes';

/**
 * The DD block's Disclosures line, off what the page already read (SR-460): the hub's summary and
 * pending lookups, and Work items' open items. No line while the hub carries no summary entry for
 * the member — unknown, not "no CRD".
 */
function hubDdDisclosuresOf(
  hub: IAdminParticipantHub,
  members: readonly IAdminParticipantDetail[],
  openItems: Readonly<Record<string, IOpenItemsRead>> | undefined
): (participantId: string) => IDdDisclosures | undefined {
  return (participantId) => {
    const summary = disclosureSummaryOf(hub, participantId);
    if (summary === undefined) return undefined;
    const member = members.find((one) => one.participantId === participantId);
    const crd = member?.identifiers.find((number) => number.idType === 'CRD')?.value ?? null;
    return {
      noCrd: summary === null,
      checked: summary?.checked ?? false,
      checkedAt: summary?.checked ? summary.refreshedAt : null,
      // As the queue reads the same row: a live count above 0 stands; none live is known only once
      // a check has landed.
      liveCount: summary?.liveCount ? summary.liveCount : summary?.checked ? 0 : null,
      openItems: openItems?.[participantId]?.total ?? null,
      crd,
      waiting: (member?.pendingLookups ?? []).some((one) => one.idType === 'CRD' && one.value === crd),
      link: recordPath(participantId)
    };
  };
}

/** The hub's sections, in U1's order. */
export function hubSections(): IHubSection[] {
  return [
    {
      key: 'overview',
      anchor: 'overview',
      label: 'Overview',
      render: ({ hub, search, written, recordPage }, anchor) => (
        <OverviewSection
          hub={hub}
          anchor={anchor}
          search={search}
          written={written}
          recordPage={recordPage}
        />
      )
    },
    {
      // Salesforce's sections first (U1): the staff fields right after Overview (F2).
      key: 'research',
      anchor: 'research',
      label: 'Veritant research',
      drawn: ({ hub, members, canReadInternal }) =>
        canReadInternal && researchDrawn(hub, members),
      // A survey tab draws each staff-form group as a section of the tab (SR-474), and the nav
      // names each of them in place of this one entry.
      entries: ({ surveyCode, researchEntries }) =>
        surveyCode === undefined ? null : researchEntries,
      render: (
        {
          hub,
          members,
          reread,
          hubRead,
          readsBegun,
          written,
          surveyCode,
          drawnDisclosures,
          crdName,
          onResearchEntries,
          openItems
        },
        anchor
      ) => (
        <ResearchSection
          hub={hub}
          members={members}
          anchor={anchor}
          writes={{ reread, hubRead, readsBegun, written }}
          partSections={[rankingsPartSection, figuresPartSection, ddPartSection(hubDdDisclosuresOf(hub, members, openItems), hub.internal)]}
          surveyCode={surveyCode}
          onEntries={surveyCode === undefined ? undefined : onResearchEntries}
          disclosures={
            // A survey tab's own copy (SR-426); the record tab draws the section itself.
            surveyCode === undefined
              ? undefined
              : (participantId, code, disclosuresAnchor) =>
                  // Null for a member with no CRD set, so a survey tab names no section for it.
                  disclosureSummaryOf(hub, participantId) ? (
                    <SurveyTabDisclosures
                      participantId={participantId}
                      surveyCode={code}
                      summary={disclosureSummaryOf(hub, participantId)}
                      drawn={drawnDisclosures.groupsOf(participantId)}
                      nameOf={crdName}
                      anchor={disclosuresAnchor}
                    />
                  ) : null
          }
        />
      )
    },
    {
      key: 'surveys',
      anchor: 'surveys',
      label: 'Surveys',
      count: ({ hub }) => hub.responses.length,
      render: ({ hub, members }, anchor) => (
        <SurveysSection responses={hub.responses} multi={members.length > 1} anchor={anchor} />
      )
    },
    {
      // U1 puts Records after Surveys.
      key: 'records',
      anchor: 'records',
      label: 'Records',
      render: ({ members }, anchor) => <RecordsSection members={members} anchor={anchor} />
    },
    {
      key: 'disclosures',
      anchor: 'disclosures',
      label: 'Disclosures',
      drawn: ({ withDisclosures }) => withDisclosures,
      render: ({ hub, members, drawnDisclosures, crdName }, anchor) => (
        <DisclosuresSection
          hub={hub}
          members={members}
          anchor={anchor}
          onDrawn={drawnDisclosures.onDrawn}
          nameOf={crdName}
        />
      )
    },
    {
      // F9's (SR-222, R10), at its U1 place after Disclosures — and always drawn, since whether a
      // member holds an item takes a read.
      key: 'work-items',
      anchor: 'work-items',
      label: 'Work items',
      render: ({ hub, members, written, drawnDisclosures, newTaskAsked, onOpenItems }, anchor) => (
        <WorkItemsSection
          onOpenItems={onOpenItems}
          hub={hub}
          members={members}
          written={written}
          disclosuresOf={drawnDisclosures.disclosuresOf}
          anchor={anchor}
          newTaskAsked={newTaskAsked}
        />
      )
    },
    {
      key: 'identity',
      anchor: IDENTITY_ANCHOR,
      label: 'Identity & numbers',
      render: ({ members, writes, recordPage }, anchor) => (
        <IdentitySection
          members={members}
          anchor={anchor}
          writes={writes}
          recordPage={recordPage}
        />
      )
    },
    {
      key: 'answers',
      anchor: 'registry-answers',
      label: 'Registry answers',
      render: ({ members, writes, secAdvElsewhere }, anchor) => (
        <RegistryAnswersSection
          members={members}
          anchor={anchor}
          writes={writes}
          secAdvElsewhere={secAdvElsewhere}
        />
      )
    },
    {
      key: 'team',
      anchor: 'team',
      label: 'Team',
      drawn: ({ teams }) => teams.length > 0,
      render: ({ teams, members, teamNotes, reread }, anchor) => (
        <TeamSection
          members={teams}
          household={members}
          notes={teamNotes}
          anchor={anchor}
          reread={reread}
        />
      )
    },
    {
      // F7's (SR-222, R1 — the plan's Q1, ruled (a)): its own section right after Team, since a
      // firm's principals have no place in a section drawn for teams alone.
      key: 'roster',
      anchor: 'roster',
      label: 'Advisors & principals',
      drawn: ({ members }) => members.some(hasRoster),
      render: ({ hub, members, search, written, reread, rosterTitle, onRoster }, anchor) => (
        <RosterSection
          title={rosterTitle}
          onRoster={onRoster}
          members={members}
          responses={hub.responses}
          search={search}
          written={written}
          reread={reread}
          anchor={anchor}
        />
      )
    },
    {
      key: 'access',
      anchor: 'access',
      label: 'Account & access',
      render: ({ hub, members, asRead, search, written, reread }, anchor) => (
        <AccountsSection
          members={members}
          responses={hub.responses}
          cards={hubMembers(asRead)}
          anchor={anchor}
          search={search}
          written={written}
          reread={reread}
        />
      )
    },
    {
      key: 'registrations',
      anchor: 'registrations',
      label: 'Registrations',
      render: ({ members }, anchor) => (
        <RegistrationsSection members={members} anchor={anchor} />
      )
    },
    {
      key: 'activity',
      anchor: 'activity',
      label: 'Activity',
      count: ({ activityTotal }) => activityTotal,
      render: ({ hub, written, onActivityTotal }, anchor) => (
        <ActivitySection
          participantId={hub.participant.participantId}
          written={written}
          anchor={anchor}
          onTotal={onActivityTotal}
        />
      )
    },
    {
      key: 'tasks',
      anchor: 'salesforce-tasks',
      label: 'Salesforce tasks',
      render: ({ members, drawnDisclosures }, anchor) => (
        <SalesforceTasksSection
          members={members}
          find={drawnDisclosures.find}
          anchor={anchor}
        />
      )
    },
    {
      key: 'notes',
      anchor: NOTES_ANCHOR,
      label: 'Notes',
      count: ({ hub }) => hub.notes.length,
      render: ({ hub, members, reread }, anchor) => (
        <NotesSection members={members} notes={hub.notes} anchor={anchor} reread={reread} />
      )
    }
  ];
}
